import { useState, useEffect, useRef } from "react";
import { useAuth, database } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { ref, push, onValue } from "firebase/database";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

export default function ChatPage() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);

  const chatId = "global-chat";

  // 🚀 Auto-scroll to the bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // 🔥 Load messages in real-time
  useEffect(() => {
    const messagesRef = ref(database, `chat/${chatId}/messages`);

    const unsubscribe = onValue(messagesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const formatted = Object.values(data).sort(
          (a, b) => a.timestamp - b.timestamp
        );

        setMessages(formatted);
        scrollToBottom();
      }
    });

    return () => unsubscribe();
  }, []);

  // ✉️ Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const messageData = {
      id: Date.now().toString(),
      sender_id: userProfile.uid,
      sender_name: userProfile.full_name,
      text: newMessage,
      timestamp: Date.now(),
    };

    try {
      await push(ref(database, `chat/${chatId}/messages`), messageData);
      setNewMessage("");
    } catch (error) {
      console.log(error);
      toast.error("Failed to send message");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-2">
            Community Chat
          </h1>
          <p className="text-gray-600">Connect with fellow CS students</p>
        </div>

        <Card className="glass border-white/50 h-[calc(100vh-260px)] flex flex-col">
          <CardHeader className="border-b">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Global Chat Room
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${
                    msg.sender_id === userProfile.uid
                      ? "flex-row-reverse"
                      : ""
                  }`}
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-gradient-to-br from-sky-400 to-cyan-500 text-white">
                      {msg.sender_name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>

                  <div
                    className={`max-w-md ${
                      msg.sender_id === userProfile.uid ? "text-right" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm" onClick={() => navigate(`/profile/${msg.sender_id}`)}>{msg.sender_name}</p>
                      <span className="text-xs text-gray-500">
                        {format(new Date(msg.timestamp), "HH:mm")}
                      </span>
                    </div>

                    <div
                      className={`inline-block p-3 rounded-2xl ${
                        msg.sender_id === userProfile.uid
                          ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white"
                          : "bg-white/70"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Bar */}
            <form
              onSubmit={handleSendMessage}
              className="border-t p-4 flex gap-2"
            >
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
              />
              <Button
                type="submit"
                className="bg-gradient-to-r from-sky-500 to-cyan-500"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
